import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { ArrowDown, ChevronsUp, Loader2 } from 'lucide-react'
import { useOrphanedMessages } from '@/components/orphaned-messages-context'
import { UserMessage } from './messages/UserMessage'
import { AssistantMessage } from './messages/AssistantMessage'
import { PlanMessage } from './messages/PlanMessage'
import { TimeoutErrorMessage } from './messages/TimeoutErrorMessage'
import { InfoMessage } from './messages/InfoMessage'
import { QuestionsMessage } from './messages/QuestionsMessage'
import { ConfirmationButtonMessage } from './messages/ConfirmationButtonMessage'
import { TurnSummaryMessage } from './messages/TurnSummaryMessage'
import { WorkflowStatusCard } from './messages/WorkflowStatusCard'
import { ProcessingErrorMessage } from './messages/ProcessingErrorMessage'
import { ProcessingIndicator } from './ProcessingIndicator'
import type { Message, Conversation, ActiveWorkflow } from '@/types/conversations'
import { removeControlTags } from '@/lib/remove-control-tags'
import { isWorkflowInProgress } from '@/lib/workflow-utils'

interface MessageListProps {
  messages: Message[]
  processing: boolean
  conversation: Conversation
  currentActivity: string | null
  onApprovePlan: (messageId: number) => Promise<void>
  onSubmitAnswers: (messageId: number, answers: Record<string, string | string[]>) => Promise<void>
  onConfirmButton: (messageId: number, prompt: string | undefined, clickedLabel?: string) => Promise<void>
  hasMoreMessages?: boolean
  isLoadingOlder?: boolean
  onLoadOlderMessages?: () => void
  collapsedMessageIds?: Set<number>
  expandedTurns?: Set<number>
  onToggleTurnExpansion?: (userMessageId: number) => void
  canUndoMessageId?: number | null
  onSuggestionClick?: (prompt: string) => void
  projectId?: number
  isRewinding?: boolean
  onUndo?: () => void
  activeWorkflow?: ActiveWorkflow | null
  onRetryMessage?: (errorMessageId: number) => Promise<void>
  scrollButtonBottomOffset?: number
  hasTaskBuilding?: boolean
  onRequestInputFocus?: () => void
  pmBrowserScreenshots?: Array<{ image: string; mediaType: string; caption: string; timestamp: number }>
}

/**
 * Build maps from turn_summary and discussion_summary messages to track
 * which messages are collapsed and which turn/discussion they belong to.
 */
function buildCollapseMaps(messages: Message[]) {
  const collapseSummaryByTurn = new Map<number, Message>()
  const collapsedCountByTurn = new Map<number, number>()

  const collapseSummaries = messages.filter(m => m.message_type === 'turn_summary')

  for (const summary of collapseSummaries) {
    if (!summary.turn_data) continue

    const turnStartId = summary.turn_data.turnStartMessageId
    collapseSummaryByTurn.set(turnStartId, summary)

    if (summary.turn_data.collapsedMessageIds) {
      collapsedCountByTurn.set(turnStartId, summary.turn_data.collapsedMessageIds.length)
    }
  }

  // Discussion summaries: keyed by the summary message's own ID
  const discussionSummaries = new Map<number, Message>()
  const discussionCollapsedIds = new Set<number>()
  const discussionCollapsedCounts = new Map<number, number>()

  const discSummaries = messages.filter(m => m.message_type === 'discussion_summary')
  for (const summary of discSummaries) {
    const ids = summary.discussion_data?.collapsedMessageIds || []
    discussionSummaries.set(summary.id, summary)
    discussionCollapsedCounts.set(summary.id, ids.length)
    ids.forEach(id => discussionCollapsedIds.add(id))
  }

  return {
    collapseSummaryByTurn,
    collapsedCountByTurn,
    discussionSummaries,
    discussionCollapsedIds,
    discussionCollapsedCounts
  }
}

/**
 * Check if a message has visible content that should be displayed
 */
function hasVisibleContent(message: Message): boolean {
  // System messages (including collapse_summary) are handled separately in filtering
  if (message.role === 'system') return true

  // User messages always have content
  if (message.role === 'user') return true

  // Interrupted messages always visible (to show the badge)
  if (message.status === 'interrupted') return true

  // Special types have their own rendering
  if (message.message_type === 'questions' || message.message_type === 'info' || message.message_type === 'plan' || message.message_type === 'confirmation' || message.message_type === 'processing_error') {
    return true
  }

  // Workflow progress messages (linked to a Workflow record)
  if (message.workflow_id) {
    return true
  }

  // For regular assistant messages, check if content is non-empty after stripping control tags
  const strippedContent = removeControlTags(message.content).trim()
  return strippedContent.length > 0
}

export function MessageList({
  messages,
  processing,
  conversation,
  currentActivity,
  onApprovePlan,
  onSubmitAnswers,
  onConfirmButton,
  hasMoreMessages = false,
  isLoadingOlder = false,
  onLoadOlderMessages,
  collapsedMessageIds = new Set(),
  expandedTurns = new Set(),
  onToggleTurnExpansion,
  canUndoMessageId,
  onSuggestionClick,
  projectId: _projectId,
  isRewinding = false,
  onUndo,
  activeWorkflow,
  onRetryMessage,
  scrollButtonBottomOffset = 24,
  onRequestInputFocus,
  pmBrowserScreenshots = []
}: MessageListProps) {
  const { t } = useTranslation('conversations')
  // Get orphaned message IDs from context
  const orphanedMessageIdSet = useOrphanedMessages()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const contentEndRef = useRef<HTMLDivElement>(null)
  const prevMessagesLengthRef = useRef(messages.length)
  const hasInitiallyScrolledRef = useRef(false)
  const [isScrollReady, setIsScrollReady] = useState(false)
  const lastUserMessageIdRef = useRef<number | null>(null)
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true)
  const [scrollViewportHeight, setScrollViewportHeight] = useState(0)
  const [spacerHeight, setSpacerHeight] = useState(0)
  const scrollThreshold = 150 // Distance from bottom to consider "at bottom"

  // Scroll to bottom helper
  const scrollToBottom = useCallback(() => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      const viewport = scrollContainer.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight
      }
    }
  }, [])

  // Measure scroll viewport height
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return
    const viewport = scrollContainer.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
    if (!viewport) return
    const observer = new ResizeObserver(([entry]) => {
      setScrollViewportHeight(entry.contentRect.height)
    })
    observer.observe(viewport)
    setScrollViewportHeight(viewport.clientHeight)
    return () => observer.disconnect()
  }, [])

  // Initial scroll on mount: scroll last user message to top
  useEffect(() => {
    if (messages.length > 0 && !hasInitiallyScrolledRef.current) {
      setTimeout(() => {
        const scrollContainer = scrollContainerRef.current
        if (!scrollContainer) return
        const viewport = scrollContainer.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
        if (!viewport) return

        // Find the last user message and scroll it to top
        const userMessages = messages.filter(m => m.role === 'user')
        const lastUser = userMessages[userMessages.length - 1]
        if (lastUser) {
          const el = scrollContainer.querySelector(`[data-message-id="${lastUser.id}"]`) as HTMLElement
          if (el) {
            const viewportRect = viewport.getBoundingClientRect()
            const elRect = el.getBoundingClientRect()
            viewport.scrollTop = elRect.top - viewportRect.top + viewport.scrollTop - 16
          }
        }
        hasInitiallyScrolledRef.current = true
        setIsScrollReady(true)
      }, 50)
    }
  }, [messages.length])

  // Listen to scroll events to detect manual scrolling
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const viewport = scrollContainer.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
    if (!viewport) return

    const handleScroll = () => {
      const scrollTop = viewport.scrollTop
      const scrollHeight = viewport.scrollHeight
      const clientHeight = viewport.clientHeight
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight

      // User scrolled to near bottom - enable auto-scroll
      if (distanceFromBottom <= scrollThreshold) {
        setAutoScrollEnabled(true)
      }
      // User scrolled up - disable auto-scroll
      else if (distanceFromBottom > scrollThreshold) {
        setAutoScrollEnabled(false)
      }
    }

    viewport.addEventListener('scroll', handleScroll, { passive: true })
    return () => viewport.removeEventListener('scroll', handleScroll)
  }, [scrollThreshold])

  // Pull-to-load: detect pull-down gesture when already at top of scroll (touch devices only)
  const [isPullTriggered, setIsPullTriggered] = useState(false)
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return
    const viewport = scrollContainer.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
    if (!viewport) return

    let touchStartY = 0
    let isPulling = false
    let hasTriggered = false

    const onTouchStart = (e: TouchEvent) => {
      // Only start tracking if we're at the very top
      if (viewport.scrollTop <= 0 && hasMoreMessages && !isLoadingOlder) {
        touchStartY = e.touches[0].clientY
        isPulling = true
        hasTriggered = false
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!isPulling || hasTriggered || !onLoadOlderMessages) return
      // Ignore if scrolled away from top during the gesture
      if (viewport.scrollTop > 0) {
        isPulling = false
        return
      }
      const pullDistance = e.touches[0].clientY - touchStartY
      if (pullDistance > 60) {
        hasTriggered = true
        isPulling = false
        setIsPullTriggered(true)
        onLoadOlderMessages()
      }
    }

    const onTouchEnd = () => {
      isPulling = false
      hasTriggered = false
    }

    viewport.addEventListener('touchstart', onTouchStart, { passive: true })
    viewport.addEventListener('touchmove', onTouchMove, { passive: true })
    viewport.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      viewport.removeEventListener('touchstart', onTouchStart)
      viewport.removeEventListener('touchmove', onTouchMove)
      viewport.removeEventListener('touchend', onTouchEnd)
    }
  }, [hasMoreMessages, isLoadingOlder, onLoadOlderMessages])

  // Clear pull indicator when loading finishes
  useEffect(() => {
    if (!isLoadingOlder) setIsPullTriggered(false)
  }, [isLoadingOlder])

  // Track message count changes (used by other effects)
  useEffect(() => {
    prevMessagesLengthRef.current = messages.length
  }, [messages.length])

  // Scroll user message to top when sent (like ChatGPT/Claude — leaves room below for streaming)
  useEffect(() => {
    const userMessages = messages.filter(m => m.role === 'user')
    const lastUserMessage = userMessages[userMessages.length - 1]
    const currentId = lastUserMessage?.id ?? null

    // Only scroll for messages from chat input (optimistic messages have negative IDs)
    if (currentId !== null && currentId !== lastUserMessageIdRef.current && currentId < 0) {
      setTimeout(() => {
        const scrollContainer = scrollContainerRef.current
        if (!scrollContainer) return
        const viewport = scrollContainer.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
        if (!viewport) return

        const messageEl = scrollContainer.querySelector(`[data-message-id="${currentId}"]`) as HTMLElement
        if (!messageEl) return

        const viewportRect = viewport.getBoundingClientRect()
        const messageRect = messageEl.getBoundingClientRect()

        // Always scroll user message to top edge (no padding — prevents visible micro-shifts when response arrives)
        const offset = messageRect.top - viewportRect.top + viewport.scrollTop
        viewport.scrollTo({ top: offset, behavior: 'smooth' })
        // Briefly disable auto-scroll during smooth animation, then re-enable
        // so streaming response is visible. If user manually scrolls up after,
        // the scroll listener will disable it again.
        setAutoScrollEnabled(false)
        setTimeout(() => setAutoScrollEnabled(true), 500)
      }, 50)
    }

    lastUserMessageIdRef.current = currentId
  }, [messages])

  // Unified auto-scroll: keeps content end visible with breathing room
  // Triggers on streaming content growth AND new messages
  const SCROLL_BREATHING_ROOM = 80
  const ensureContentVisible = useCallback(() => {
    const scrollContainer = scrollContainerRef.current
    const marker = contentEndRef.current
    if (!scrollContainer || !marker) return
    const viewport = scrollContainer.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
    if (!viewport) return
    const viewportRect = viewport.getBoundingClientRect()
    const markerRect = marker.getBoundingClientRect()
    // Only scroll when content is actually clipped below the viewport
    if (markerRect.bottom > viewportRect.bottom) {
      const target = markerRect.bottom - viewportRect.top + viewport.scrollTop - viewport.clientHeight + SCROLL_BREATHING_ROOM
      viewport.scrollTo({ top: target, behavior: 'smooth' })
    }
  }, [])

  // Auto-scroll during PM streaming (content grows within a single message)
  const lastStreamingContentLengthRef = useRef(0)
  const streamingContentLength = useMemo(() => {
    if (!processing) return 0
    const lastMsg = messages[messages.length - 1]
    return lastMsg?.status === 'processing' ? (lastMsg.content?.length || 0) : 0
  }, [messages, processing])

  useEffect(() => {
    if (autoScrollEnabled && streamingContentLength > lastStreamingContentLengthRef.current) {
      ensureContentVisible()
    }
    lastStreamingContentLengthRef.current = streamingContentLength
  }, [streamingContentLength, autoScrollEnabled, ensureContentVisible])

  // Auto-scroll when new messages arrive (new chunks, turn summary)
  useEffect(() => {
    if (autoScrollEnabled && messages.length > prevMessagesLengthRef.current) {
      ensureContentVisible()
    }
  }, [messages.length, autoScrollEnabled, ensureContentVisible])

  // Auto-scroll when activity indicator changes (ProcessingIndicator height changes)
  useEffect(() => {
    if (autoScrollEnabled && currentActivity && processing) {
      ensureContentVisible()
    }
  }, [currentActivity, processing, autoScrollEnabled, ensureContentVisible])

  // When processing ends, ensure final content is visible
  // For discussion/questions responses, scroll the response into view (not to bottom)
  const prevProcessingRef = useRef(processing)
  useEffect(() => {
    if (prevProcessingRef.current && !processing) {
      // Find the last assistant message to check if it's a discussion response
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')
      const isDiscussionResponse = lastAssistant?.source === 'discussion' || lastAssistant?.message_type === 'questions'

      if (isDiscussionResponse) {
        setTimeout(() => {
          const scrollContainer = scrollContainerRef.current
          if (!scrollContainer) return
          const messageEl = scrollContainer.querySelector(`[data-message-id="${lastAssistant.id}"]`) as HTMLElement
          if (!messageEl) return
          const viewport = scrollContainer.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
          if (!viewport) return
          const viewportRect = viewport.getBoundingClientRect()
          const messageRect = messageEl.getBoundingClientRect()

          const isQuestions = lastAssistant?.message_type === 'questions'

          if (isQuestions || messageRect.height <= viewport.clientHeight - 32) {
            // Questions or short discussion: scroll to top of message
            const offset = messageRect.top - viewportRect.top + viewport.scrollTop - 16
            viewport.scrollTo({ top: offset, behavior: 'smooth' })
          } else {
            // Long discussion: show bottom so user sees the end and can reply
            const offset = messageRect.bottom - viewportRect.top + viewport.scrollTop - viewport.clientHeight + SCROLL_BREATHING_ROOM
            viewport.scrollTo({ top: offset, behavior: 'smooth' })
          }

          // Focus input for discussion responses (questions have their own inline form)
          if (!isQuestions && onRequestInputFocus) {
            onRequestInputFocus()
          }
        }, 100)
      } else if (autoScrollEnabled) {
        setTimeout(() => ensureContentVisible(), 100)
      }
    }
    prevProcessingRef.current = processing
  }, [processing, autoScrollEnabled, messages, ensureContentVisible, onRequestInputFocus])

  // Latest builder streaming chunk text — shown in ProcessingIndicator instead of as a message
  const latestStreamingText = useMemo(() => {
    if (!processing) return null
    // Scan backward for the latest builder chunk, stopping at any turn boundary
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      // Stop at turn boundaries
      if (m.role === 'user') break
      if (m.message_type === 'discussion_summary') break
      if (m.message_type === 'turn_summary') break
      // Skip non-builder messages (PM discussion, system, etc.)
      if (m.role !== 'assistant') continue
      if (m.source === 'discussion' || m.source === 'prototype') continue
      if (m.message_type !== 'regular') continue
      if (m.workflow_id) continue
      if (m.task_id) continue  // Task build messages go to TaskBadge, not ProcessingIndicator
      // Found a builder chunk
      const cleaned = removeControlTags(m.content).trim()
      if (cleaned) return cleaned
    }
    return null
  }, [messages, processing])

  // Memoize the rendered message list
  const renderedMessages = useMemo(() => {
    // Build maps from collapse_summary messages
    const {
      collapseSummaryByTurn,
      collapsedCountByTurn
    } = buildCollapseMaps(messages)

    // Filter messages for rendering
    const filteredMessages = messages.filter(message => {
      // Always keep turn_summary messages (including task build summaries)
      if (message.message_type === 'turn_summary') return true

      // Hide task log messages (PM-only: task actions, auto-run toggles)
      if (message.message_type === 'task_log') return false

      // Keep non-task info messages. Hide task-related info.
      if (message.message_type === 'info') return !message.task_id

      // Hide task build messages from chat — they belong to the board/badge
      if (message.task_id) return false

      // Filter out discussion_summary and other system messages
      if (message.role === 'system') return false

      // Check if this message is collapsed by a turn summary
      const isCollapsed = collapsedMessageIds.has(message.id)

      if (isCollapsed) {
        // Find which turn this message belongs to
        let turnStartId: number | undefined

        for (const [turnId, summary] of collapseSummaryByTurn.entries()) {
          if (summary.turn_data?.collapsedMessageIds?.includes(message.id)) {
            turnStartId = turnId
            break
          }
        }

        if (turnStartId !== undefined) {
          const isTurnExpanded = expandedTurns.has(turnStartId)
          if (!isTurnExpanded) return false
          return true
        }
      }

      // Hide builder assistant messages — they show via turn_summary purple card or ProcessingIndicator.
      // Without this, they flash with a green line between processing end and turn_summary arrival.
      const isBuilderMsg = message.role === 'assistant' &&
        message.message_type === 'regular' &&
        message.source !== 'discussion' &&
        message.source !== 'prototype' &&
        !message.workflow_id
      if (isBuilderMsg) return false

      // Otherwise, check for visible content
      return hasVisibleContent(message)
    })

    // Reorder: move info messages that appear before turn_summary to after it
    // This ensures "Processing interrupted" etc. appear at the bottom of the turn
    const reorderedMessages: Message[] = []
    for (let i = 0; i < filteredMessages.length; i++) {
      const msg = filteredMessages[i]
      const nextMsg = filteredMessages[i + 1]

      // If this is an info message and next is turn_summary, skip it (we'll add it after)
      if (msg.message_type === 'info' && nextMsg?.message_type === 'turn_summary') {
        continue
      }

      reorderedMessages.push(msg)

      // If this is turn_summary and previous was info, add the info after
      if (msg.message_type === 'turn_summary') {
        const prevMsg = filteredMessages[i - 1]
        if (prevMsg?.message_type === 'info') {
          reorderedMessages.push(prevMsg)
        }
      }
    }

    // During processing, hide ALL builder assistant chunks (they show in ProcessingIndicator instead)
    const streamingHiddenIds = new Set<number>()
    if (processing) {
      // Find last turn boundary
      let turnBoundaryIdx = -1
      for (let i = reorderedMessages.length - 1; i >= 0; i--) {
        const m = reorderedMessages[i]
        if (m.role === 'user' || m.message_type === 'discussion_summary') {
          turnBoundaryIdx = i
          break
        }
      }
      if (turnBoundaryIdx >= 0) {
        const streamingMsgs = reorderedMessages
          .slice(turnBoundaryIdx + 1)
          .filter(m =>
            m.role === 'assistant' &&
            m.message_type === 'regular' &&
            m.source !== 'discussion' &&
            m.source !== 'prototype' &&
            !m.workflow_id &&
            !m.task_id
          )
        // Hide all builder chunks — latest text shows in ProcessingIndicator
        streamingMsgs.forEach(m => streamingHiddenIds.add(m.id))
      }
    }

    const result: React.ReactNode[] = []

    // Find last user message ID for undo button
    const userMessagesInView = reorderedMessages.filter(m => m.role === 'user')
    const lastUserMessageId = userMessagesInView[userMessagesInView.length - 1]?.id

    // Track whether we're in an "orphaned" section (after orphaned user message)
    let inOrphanedSection = false

    // Render messages
    reorderedMessages.forEach((message, index) => {
      // Skip streaming messages hidden by crossfade (show only the latest)
      if (streamingHiddenIds.has(message.id)) return

      const isFirstMessage = index === 0
      const nextMessage = reorderedMessages[index + 1]
      const isLastAssistantBeforeUser = message.role === 'assistant' && nextMessage?.role === 'user'
      const isLastMessage = index === reorderedMessages.length - 1
      const showTimestamp = isLastAssistantBeforeUser || isLastMessage
      const showDivider = message.role === 'user' && !isFirstMessage

      // Determine if this message should be grayed out
      let isOrphaned = false
      if (message.role === 'user') {
        // User message: check if it's in the orphaned list
        isOrphaned = orphanedMessageIdSet.has(message.id)
        // Update orphaned section tracker for subsequent assistant messages
        inOrphanedSection = isOrphaned
      } else if (message.role === 'assistant') {
        // Assistant message: gray if the preceding user message was orphaned
        isOrphaned = inOrphanedSection
      }

      // Render the actual message
      // Use message ID as key - it's immutable and prevents duplicate key errors
      const stableKey = `message-${message.id}`
      const isDiscussionMessage = message.source === 'discussion' && message.role !== 'user'
      const isBuilderMessage = message.role === 'assistant' && message.source !== 'discussion' && message.source !== 'prototype' && message.message_type === 'regular'
      const borderClass = isDiscussionMessage
        ? 'pl-3 border-l-2 border-blue-300/50 dark:border-blue-500/30'
        : isBuilderMessage
        ? 'pl-3 border-l-2 border-emerald-300/50 dark:border-emerald-500/30'
        : ''
      result.push(
        <div key={stableKey} className={borderClass} data-message-id={message.id}>
          {showDivider && (
            <div className="border-b border-border/60 my-4" />
          )}

          {/* Render based on message type */}
          {message.role === 'user' && (
            <UserMessage
              message={message}
              isLastUserMessage={message.id === lastUserMessageId}
              canUndoMessageId={canUndoMessageId}
              isRewinding={isRewinding}
              onUndo={onUndo}
              isOrphaned={isOrphaned}
            />
          )}

          {message.message_type === 'plan' && (
            <PlanMessage
              message={message}
              conversation={conversation}
              onApprovePlan={onApprovePlan}
              isProcessing={processing}
            />
          )}

          {message.message_type === 'timeout_error' && (
            <TimeoutErrorMessage message={message} />
          )}

          {message.message_type === 'processing_error' && (() => {
            const errorIdx = messages.findIndex(m => m.id === message.id)
            const hasMessageAfter = messages.slice(errorIdx + 1).some(m => m.role === 'user' || (m.role === 'assistant' && m.message_type !== 'processing_error'))

            return (
              <ProcessingErrorMessage
                message={message}
                isProcessing={processing}
                onRetry={!hasMessageAfter && onRetryMessage
                  ? () => onRetryMessage(message.id)
                  : undefined}
              />
            )
          })()}

          {message.message_type === 'info' && !message.workflow_id && (
            <InfoMessage message={message} />
          )}

          {message.message_type === 'questions' && (
            <QuestionsMessage
              message={message}
              onSubmitAnswers={onSubmitAnswers}
              isProcessing={processing}
            />
          )}

          {message.message_type === 'confirmation' && (
            <ConfirmationButtonMessage
              message={message}
              onConfirm={onConfirmButton}
              isProcessing={processing}
            />
          )}

          {(message.workflow_id || activeWorkflow?.message_id === message.id) && (
            <WorkflowStatusCard message={message} activeWorkflow={activeWorkflow} />
          )}

          {message.message_type === 'turn_summary' && message.turn_data && (
            <TurnSummaryMessage
              message={message}
              isOrphaned={orphanedMessageIdSet.has(message.turn_data.turnStartMessageId)}
              onSuggestionClick={onSuggestionClick}
              collapsedMessages={
                message.turn_data.collapsedMessageIds
                  ? messages.filter(m => message.turn_data!.collapsedMessageIds!.includes(m.id))
                  : []
              }
            />
          )}

          {message.role === 'assistant' &&
           message.message_type !== 'plan' &&
           message.message_type !== 'timeout_error' &&
           message.message_type !== 'processing_error' &&
           message.message_type !== 'info' &&
           message.message_type !== 'questions' &&
           message.message_type !== 'confirmation' &&
           message.message_type !== 'turn_summary' &&
           message.message_type !== 'discussion_summary' &&
           !message.workflow_id && (
            <AssistantMessage
              message={message}
              isStreaming={message.status === 'processing'}
              showTimestamp={showTimestamp}
              isOrphaned={isOrphaned}
            />
          )}
        </div>
      )
    })

    return result
  }, [messages, collapsedMessageIds, expandedTurns, onToggleTurnExpansion, onApprovePlan, onSubmitAnswers, onConfirmButton, processing, conversation, canUndoMessageId, isRewinding, onUndo, orphanedMessageIdSet, activeWorkflow])

  // Recalculate spacer: breathing room by default, full scroll room only when content overflows
  useEffect(() => {
    // Enough space for the NextStepsPanel overlay (absolute positioned at bottom)
    const MIN_BOTTOM_GAP = 140

    if (!scrollViewportHeight) return
    const scrollContainer = scrollContainerRef.current
    const marker = contentEndRef.current
    if (!scrollContainer || !marker) return

    const userMessages = messages.filter(m => m.role === 'user')
    const lastUser = userMessages[userMessages.length - 1]
    if (!lastUser) { setSpacerHeight(MIN_BOTTOM_GAP); return }

    const userEl = scrollContainer.querySelector(`[data-message-id="${lastUser.id}"]`) as HTMLElement
    if (!userEl) { setSpacerHeight(0); return }

    const viewport = scrollContainer.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement
    if (!viewport) return
    const markerRect = marker.getBoundingClientRect()
    const userRect = userEl.getBoundingClientRect()
    const contentBelowUser = markerRect.top - userRect.top
    setSpacerHeight(Math.max(MIN_BOTTOM_GAP, scrollViewportHeight - contentBelowUser))
  }, [messages, renderedMessages, scrollViewportHeight, processing])

  return (
    <ScrollArea className={`h-full w-full transition-opacity duration-150 ${messages.length === 0 || isScrollReady ? 'opacity-100' : 'opacity-0'}`} ref={scrollContainerRef}>
      {messages.length === 0 ? (
        <div className="flex items-center justify-center h-full p-8">
          <div className="text-center text-muted-foreground">
            <p className="text-sm">{t('chat.noMessages')}</p>
            <p className="text-xs mt-1">{t('chat.noMessagesDescription')}</p>
          </div>
        </div>
      ) : (
      <>
      <div className="pr-4 pl-4 w-full min-w-0 overflow-hidden">
        {/* Load Older Messages */}
        {hasMoreMessages && onLoadOlderMessages && (
          <div className="mb-4 flex justify-center">
            {isLoadingOlder ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <Button
                onClick={onLoadOlderMessages}
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full"
                title={t('chat.loadOlderMessages')}
              >
                <ChevronsUp className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}

        {renderedMessages}

        {/* Processing indicator - hide during git operations and workflows (workflow card shows its own progress) */}
        {processing && conversation.git_operation_status === 'idle' && !isWorkflowInProgress(activeWorkflow) && (
          <ProcessingIndicator hasActivity={!!currentActivity} streamingText={latestStreamingText} isPmStreaming={streamingContentLength > 0} activityText={currentActivity} />
        )}

        {/* Content end marker — auto-scroll targets this, not scrollHeight */}
        <div ref={contentEndRef} />

        {/* Spacer — exactly enough to scroll last user message to top, no excess */}
        {spacerHeight > 0 && <div style={{ height: spacerHeight }} />}
      </div>

      {/* PM browser screenshot — floating bottom-right, swaps in place */}
      {pmBrowserScreenshots.length > 0 && (() => {
        const latest = pmBrowserScreenshots[pmBrowserScreenshots.length - 1]
        return (
          <div
            key={latest.timestamp}
            className="absolute right-4 z-20 w-96 rounded-lg border border-border/50 bg-background/95 backdrop-blur-sm shadow-lg overflow-hidden animate-in fade-in duration-300"
            style={{ bottom: scrollButtonBottomOffset + 16 }}
          >
            <div className="px-2.5 py-1 text-xs text-muted-foreground border-b border-border/30 flex items-center justify-between gap-2">
              <span className="truncate min-w-0">{latest.caption}</span>
              <span className="text-[10px] opacity-50 flex-shrink-0">PM preview</span>
            </div>
            <img
              src={`data:${latest.mediaType};base64,${latest.image}`}
              alt={latest.caption}
              className="w-full"
            />
          </div>
        )
      })()}

      {/* Scroll to bottom button */}
      {!autoScrollEnabled && (
        <Button
          onClick={scrollToBottom}
          size="icon"
          variant="secondary"
          className="absolute left-1/2 -translate-x-1/2 rounded-full shadow-sm hover:shadow-lg transition-all z-10 backdrop-blur-[2px] bg-black/[0.01] dark:bg-white/3 border border-black/2 dark:border-white/4 hover:!bg-secondary/80"
          style={{ bottom: scrollButtonBottomOffset + 16 }}
          title={t('chat.scrollToBottom')}
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      )}
      </>
      )}
    </ScrollArea>
  )
}
